/**
 * Сторінка «Історія» (редизайн v2): тонка лінія прогресу по низу картки, свайпи замість кнопки видалення.
 * Стара версія збережена в history.legacy.ts — це точка відкату (docs/history-v2.md):
 *  - HISTORY_V2_ENABLED = false → завжди стара;
 *  - /history.html?design=old | ?design=new — перемикання без збірки (запам'ятовується в localStorage).
 */
import { api, type HistoryItem } from '../api';
import { requireAuthOrRedirect } from '../auth';
import { renderNav } from '../nav';
import { coverEl, fmtDateGroup, fmtTime, toast, pluginContentTypes, workHref } from '../utils';
import { warmSlugCache } from '../pretty-cache';
import { initHistoryLegacy, readerUrl } from './history.legacy';

export const HISTORY_V2_ENABLED = true;
const DESIGN_KEY = 'history-design';
const SCROLL_KEY = 'history-scroll';

function useLegacy(): boolean {
  if (!HISTORY_V2_ENABLED) return true;
  try {
    const q = new URLSearchParams(location.search).get('design');
    if (q === 'old') localStorage.setItem(DESIGN_KEY, 'old');
    if (q === 'new') localStorage.removeItem(DESIGN_KEY);
    return localStorage.getItem(DESIGN_KEY) === 'old';
  } catch {
    return false;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

function mmss(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

type Kind = 'novel' | 'manga' | 'anime';
interface Prog {
  /** done — дочитано/позначено; part — є реальний прогрес; none — даних немає (не вигадуємо 0–9%). */
  state: 'done' | 'part' | 'none';
  pct: number;
  text: string;
}

/**
 * Прогрес запису. Для аніме позиція плеєра ненадійна (iframe-плеєри її не віддають, у решти — шум 0–9%),
 * тому головний сигнал — відмітка «переглянуто» (chapterRead); час показуємо лише коли він справжній (> 60 с).
 */
function progressOf(h: HistoryItem, kind: Kind): Prog {
  if (kind === 'anime') {
    if (h.chapterRead) return { state: 'done', pct: 100, text: 'переглянуто' };
    const pos = h.positionSeconds ?? 0;
    const dur = h.durationSeconds ?? 0;
    if (pos > 60 && dur > 0) return { state: 'part', pct: Math.min(99, Math.round((pos / dur) * 100)), text: `${mmss(pos)} / ${mmss(dur)}` };
    return { state: 'none', pct: 0, text: 'відкрито, без відмітки' };
  }
  if (h.progress >= 100 || h.chapterRead) return { state: 'done', pct: 100, text: 'дочитано' };
  if (h.totalPages) return { state: 'part', pct: h.progress, text: `стор. ${h.page ?? 1} з ${h.totalPages}` };
  if (h.progress > 0) return { state: 'part', pct: h.progress, text: `${h.progress}%` };
  return { state: 'none', pct: 0, text: 'відкрито' };
}

const ICON_BOOK =
  '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>';
const ICON_HIDE =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><path d="M2 2l20 20"/></svg>';

/**
 * У розмітці (public/history.html) кнопок пошуку й очищення та рядка пошуку більше немає.
 * Стара версія (відкат: ?design=old або HISTORY_V2_ENABLED = false) без них не працює,
 * тож створюємо їх тут — до запуску старого коду.
 */
function ensureLegacyControls() {
  if (document.getElementById('history-search-toggle')) return;
  const row = document.getElementById('history-title-row');
  const box = row?.querySelector('.detail-actions');
  if (!row || !box) return;
  box.innerHTML =
    '<button id="history-search-toggle" class="iconbtn" aria-label="Пошук в історії"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg></button>' +
    '<button id="history-clear-btn" class="iconbtn" aria-label="Очистити список історії"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h13M3 12h9M3 18h5"/><path d="M15 12l6 6m0-6l-6 6"/></svg></button>';
  const bar = document.createElement('div');
  bar.className = 'searchbar';
  bar.id = 'history-searchbar';
  bar.style.display = 'none';
  bar.innerHTML =
    '<svg class="sb-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>' +
    '<input type="text" id="search" placeholder="Шукати в історії">' +
    '<button id="history-search-close" class="iconbtn" aria-label="Закрити пошук"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>';
  row.insertAdjacentElement('afterend', bar);
}

export async function initHistory() {
  if (useLegacy()) {
    ensureLegacyControls();
    return initHistoryLegacy();
  }

  if (!(await requireAuthOrRedirect())) return;
  document.body.classList.add('history-v2');
  renderNav('history');
  const contentTypes = await pluginContentTypes();
  const kindOf = (pluginId: string): Kind => contentTypes[pluginId] ?? 'manga';

  const list = document.getElementById('list') as HTMLElement;
  const empty = document.getElementById('empty') as HTMLElement;

  let items: HistoryItem[] = [];
  let firstRender = true;

  // ----- прокрутка: стрілка «назад» повертає на те саме місце -----
  const scroller = (): HTMLElement => (document.querySelector('.content') as HTMLElement) || document.documentElement;
  function rememberScroll() {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ c: scroller().scrollTop, w: window.scrollY }));
    } catch {
      /* ок */
    }
  }
  function restoreScroll() {
    try {
      const raw = sessionStorage.getItem(SCROLL_KEY);
      sessionStorage.removeItem(SCROLL_KEY);
      if (!raw) return;
      const s = JSON.parse(raw) as { c: number; w: number };
      const apply = () => {
        scroller().scrollTop = s.c;
        window.scrollTo(0, s.w);
      };
      apply();
      requestAnimationFrame(apply);
    } catch {
      /* ок */
    }
  }

  /**
   * Куди веде картка (тап і свайп вліво): недочитаний розділ — у читалку з тієї ж позиції; дочитаний/позначений
   * розділ і всі аніме — на сторінку твору (там кнопка «Читати» веде на наступний непрочитаний, а плеєр аніме
   * сам відкриває потрібну серію).
   */
  function openHref(h: HistoryItem): string {
    const kind = kindOf(h.pluginId);
    if (kind !== 'anime' && progressOf(h, kind).state !== 'done') return readerUrl(h, contentTypes);
    return workHref(h.pluginId, h.novelPath, kind, '&from=history');
  }

  function removeCard(h: HistoryItem, root: HTMLElement) {
    const prev = items;
    items = items.filter((i) => i.novelId !== h.novelId);
    root.style.height = `${root.offsetHeight}px`;
    void root.offsetHeight;
    root.classList.add('h2-gone');
    root.style.height = '0px';
    window.setTimeout(() => render(), 320);
    api.removeHistoryItem(h.novelId).catch((err) => {
      items = prev;
      toast((err as Error).message);
      render();
    });
  }

  /** Свайп: вліво — продовжити (колір теми), вправо — приховати з історії (жовтий). */
  function attachSwipe(h: HistoryItem, root: HTMLElement, head: HTMLElement) {
    let sx = 0, sy = 0, dx = 0, active = false, dragging = false, id = -1;
    let suppress = false;
    const width = () => head.offsetWidth || 360;
    const threshold = () => Math.max(80, width() * 0.3);
    const setX = (x: number) => {
      dx = x;
      head.style.transform = `translateX(${x}px)`;
      root.classList.toggle('sw-r', x > 0); // вправо — «Приховати»
      root.classList.toggle('sw-l', x < 0); // вліво — «Продовжити»
      root.style.setProperty('--sw', String(Math.min(1, Math.abs(x) / threshold())));
    };
    head.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      active = true; dragging = false; sx = e.clientX; sy = e.clientY; dx = 0; id = e.pointerId;
    });
    head.addEventListener('pointermove', (e) => {
      if (!active || e.pointerId !== id) return;
      const mx = e.clientX - sx;
      const my = e.clientY - sy;
      if (!dragging) {
        if (Math.abs(my) > 10 && Math.abs(my) > Math.abs(mx)) { active = false; return; } // це прокрутка
        if (Math.abs(mx) < 8 || Math.abs(mx) < Math.abs(my)) return;
        dragging = true;
        head.classList.add('drag');
        try { head.setPointerCapture(id); } catch { /* ок */ }
      }
      setX(Math.max(-width(), Math.min(width(), mx)));
    });
    const finish = (e: PointerEvent) => {
      if (!active || e.pointerId !== id) return;
      active = false;
      if (!dragging) return;
      dragging = false;
      suppress = true;
      window.setTimeout(() => (suppress = false), 350);
      head.classList.remove('drag');
      if (e.type === 'pointerup' && dx <= -threshold()) {
        rememberScroll();
        head.style.transform = `translateX(${-width()}px)`;
        location.href = openHref(h);
        window.setTimeout(() => setX(0), 600); // якщо перехід не відбувся (bfcache) — картка на місце
      } else if (e.type === 'pointerup' && dx >= threshold()) {
        head.style.transform = `translateX(${width()}px)`;
        removeCard(h, root);
      } else {
        setX(0);
        root.classList.remove('sw-r', 'sw-l');
      }
    };
    head.addEventListener('pointerup', finish);
    head.addEventListener('pointercancel', finish);
    head.addEventListener('click', (e) => { if (suppress) { e.preventDefault(); e.stopPropagation(); } }, true);
    head.addEventListener('dragstart', (e) => e.preventDefault());
  }

  function buildItem(h: HistoryItem, n: number): HTMLElement {
    const kind = kindOf(h.pluginId);
    const pr = progressOf(h, kind);
    const root = el('div', `h2-item ${pr.state}`);
    root.style.setProperty('--n', String(Math.min(n, 12)));

    const actOpen = el('div', 'h2-act open');
    actOpen.innerHTML = `<span>${pr.state === 'done' && kind !== 'anime' ? 'Далі' : 'Продовжити'}</span>${ICON_BOOK}`;
    const actDel = el('div', 'h2-act del');
    actDel.innerHTML = `${ICON_HIDE}<span>Приховати</span>`;

    const head = el('div', 'h2-head');
    const cov = el('a', 'h2-cov');
    cov.href = workHref(h.pluginId, h.novelPath, kind, '&from=history');
    cov.setAttribute('aria-label', `Сторінка твору: ${h.novelName}`);
    const cover = coverEl(h.novelName, h.cover, h.novelId);
    cover.classList.add('cover');
    cov.append(cover);
    cov.addEventListener('click', rememberScroll);

    const main = el('a', 'h2-main');
    main.href = openHref(h);
    main.addEventListener('click', rememberScroll);
    const row = el('div', 'h2-row');
    row.append(el('span', 'h2-num', pr.text), el('span', 'h2-time', fmtTime(h.updatedAt)));
    const sub = el('p', 'h2-sub');
    sub.append(el('b', '', h.chapterName));
    main.append(el('p', 'h2-title', h.novelName), sub, row);

    const bar = el('i', 'h2-bar');
    bar.style.setProperty('--p', String(pr.pct));
    head.append(cov, main, bar);
    root.append(actOpen, actDel, head);
    attachSwipe(h, root, head);
    return root;
  }

  function render() {
    const entries = items;
    list.innerHTML = '';
    list.classList.toggle('h2-enter', firstRender);
    if (!entries.length) {
      empty.style.display = 'flex';
      list.style.display = 'none';
      return;
    }
    empty.style.display = 'none';
    list.style.display = 'block';
    let group = '';
    entries.forEach((h, i) => {
      const g = fmtDateGroup(h.updatedAt);
      if (g !== group) {
        group = g;
        list.append(el('div', 'section-label', g));
      }
      list.append(buildItem(h, i));
    });
    firstRender = false;
  }

  async function load() {
    try {
      items = await api.history();
    } catch (err) {
      toast((err as Error).message);
      items = [];
    }
    render();
    restoreScroll();
    warmSlugCache(
      items.map((h) => ({ pluginId: h.pluginId, path: h.novelPath, title: h.novelName })),
      () => render(),
    );
  }

  await load();
}
